import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import SplitFlap from "@/components/site/SplitFlap";
import StreakIcon from "@/components/app/StreakIcon";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const START_STREAK = 41;
// Thursday is the day you go quiet and get nudged.
const NUDGE_DAY = 3;

const notes = [
  "Mon · Both checked in before 7.",
  "Tue · Two focus rooms, timers in sync.",
  "Wed · Aanya verified your mock test.",
  "Thu · You went quiet at 4. Aanya nudged. Done by 5.",
  "Fri · You logged 5.5 hours. She logged 6.",
  "Sat · Long run, then a short check-in.",
  "Sun · Week closed. Nobody skipped.",
];

// A looping, illustrative week: you check in, the partner follows, the shared streak ticks.
export default function PairLedger() {
  const reduce = useReducedMotion();
  const CYCLE = 7 * 3 + 5;
  const [tick, setTick] = useState(reduce ? 7 * 3 : 0);

  useEffect(() => {
    if (reduce) return undefined;
    const id = window.setInterval(() => setTick((t) => (t + 1) % (CYCLE * 7)), 620);
    return () => window.clearInterval(id);
  }, [reduce, CYCLE]);

  const step = tick % CYCLE;
  const week = Math.floor(tick / CYCLE);

  // Each day takes three beats: you, partner (or nudge), settle.
  const dayIndex = Math.min(Math.floor(step / 3), 6);
  const beat = step % 3;
  const youState = (d) => {
    if (step >= 21 || d < dayIndex) return "done";
    if (d > dayIndex) return "empty";
    if (d === NUDGE_DAY) return beat === 0 ? "empty" : beat === 1 ? "nudged" : "done";
    return "done";
  };
  const partnerState = (d) => {
    if (step >= 21 || d < dayIndex) return "done";
    if (d > dayIndex) return "empty";
    return beat >= 1 ? "done" : "empty";
  };
  const closedDays = step >= 21 ? 7 : dayIndex + (partnerState(dayIndex) === "done" && youState(dayIndex) === "done" ? 1 : 0);
  const streak = String(START_STREAK + week * 7 + closedDays).padStart(2, "0");
  const note = notes[Math.min(step >= 21 ? 6 : dayIndex, 6)];

  return (
    <figure className="ledger relative rounded-lg border border-white/10 bg-[#0e0e0e]/80 p-5 sm:p-7" aria-label="Illustration: a week where you and your partner both check in daily and the shared streak grows from 41 to 48 days.">
      <div aria-hidden="true">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="font-serif-editorial text-2xl leading-none text-[#F5F5F0]">This week, together</div>
            <div className="mt-2 text-xs text-[#8a8a92]">Illustrative · names are examples</div>
          </div>
          <div className="text-right">
            <SplitFlap value={streak} className="flap-sm" label={`${streak} day streak`} />
            <div className="mt-2 flex items-center justify-end gap-1.5 text-[11px] text-[#A1A1AA] tracking-wide"><StreakIcon className="h-3.5 w-3.5 text-[#F59E0B]" /> day shared streak</div>
          </div>
        </div>

        <div className="mt-7 grid grid-cols-[4.5rem_1fr] gap-y-3 items-center">
          <div />
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {DAYS.map((d, i) => (
              <div key={d} className={`text-center text-[11px] tabular-nums transition-colors duration-300 ${i === dayIndex && step < 21 ? "text-[#F59E0B]" : "text-[#6f6f76]"}`}>{d}</div>
            ))}
          </div>

          <div className="text-sm text-[#F5F5F0]">You</div>
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {DAYS.map((d, i) => <Cell key={d} state={youState(i)} />)}
          </div>

          <div className="text-sm text-[#F5F5F0]">Aanya</div>
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {DAYS.map((d, i) => <Cell key={d} state={partnerState(i)} />)}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-white/[0.07] min-h-[2.75rem]">
          <motion.p
            key={note}
            initial={reduce ? false : { opacity: 0, y: 6, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-sm text-[#A1A1AA] font-light"
          >
            {note}
          </motion.p>
        </div>
      </div>
    </figure>
  );
}

function Cell({ state }) {
  return (
    <span className={`ledger-cell ledger-cell--${state}`}>
      <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" aria-hidden="true">
        <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
